import React from 'react';
import { of, concat, merge } from 'rxjs';
import { FormattedMessage } from 'react-intl';

import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import Global from '../../../store/global-selectors';
import * as BulkActions from '../../../store/bulk/bulk.actions';
import * as Actions from '../../../store/bulk/purchase/bulk.purchase.actions';
import { LabelBatchStatusStep } from '../../../enums/label-batch-status-step';
import { CODES_THAT_SHOW_COST_REVIEW, determineShouldShowLabelBatchStatus } from '../../quickship/quickship.helpers';
import { handleBulkPurchaseEnd } from './bulk.purchase.observables';
import { getPlansWithDangerousGoods, trackBulkLabelCreationComplete } from '../../labels/tracking-events';

import { messages } from './messages';

const getFpidsWithLabelsCreated = (included, errored) => included.filter((id) => !errored.includes(id));

export const showErrorWithBulkOrdersNotification = (count = 0, dontHide = true) => {
  const props = {
    title: <FormattedMessage {...messages.errorBulkPurchaseTitle} />,
    content: <FormattedMessage {...messages.errorBulkPurchaseContent} values={{ count }} />,
    notificationType: NOTIFICATION_TYPE_ERROR,
    dontHide,
    duration: 3000,
  };
  return NotificationService.showNotification({ ...props });
};

export const showPurchasingLabelsStatusMessage = (count = 0, dontHide = true) => {
  const props = {
    title: <FormattedMessage {...messages.purchasingBulkLabelsTitle} />,
    content: <FormattedMessage {...messages.purchasingBulkLabelsContent} values={{ count }} />,
    notificationType: 'info',
    dontHide,
  };
  return NotificationService.showNotification({ ...props });
};

export const showPurchasingLabelsSuccessMessage = ({ successCount = 0, errorCount = 0, warningCount = 0 }) => {
  let props = {
    title: <FormattedMessage {...messages.purchasingBulkLabelsSuccessTitle} />,
    content: <FormattedMessage {...messages.purchasingBulkLabelsSuccessContent} values={{ count: successCount }} />,
    notificationType: NOTIFICATION_TYPE_SUCCESS,
    duration: 3000,
  };

  if (warningCount) {
    props = {
      ...props,
      content: <FormattedMessage {...messages.purchasingBulkLabelsSuccessWithWarningsContent} values={{ successCount, warningCount }} />,
    };
  }

  // if there are errors, prefer the "mixed" result over success+warning results
  if (errorCount) {
    props = {
      ...props,
      notificationType: NOTIFICATION_TYPE_ERROR,
      title: <FormattedMessage {...messages.purchasingHadSomeErrorsTitle} />,
      content: <FormattedMessage {...messages.purchasingBulkLabelsMixedContent} values={{ successCount, errorCount }} />,
    };
  }

  return NotificationService.showNotification({ ...props });
};

export const handleBulkPurchaseProcess = ({ included, excluded, errored, andPrint, quickshipProps, lbsModalId = '', getState }) => {
  let purchasingNotificationId = '';
  return ({ apiSuccess, labelBatchIds, finalMessage, errorMessage, errorResponse }) => {
    const quickshipShouldHideLabelBatchStatusModal = !determineShouldShowLabelBatchStatus(quickshipProps) && !!lbsModalId;
    const state = getState();

    if (apiSuccess) {
      const apiSuccessActions = [BulkActions.bulkPurchaseLabelSuccess(included, excluded, errored, labelBatchIds, andPrint)];

      // only show notification if no modal
      if (!lbsModalId) {
        purchasingNotificationId = showPurchasingLabelsStatusMessage(included.length);
      }

      if (!determineShouldShowLabelBatchStatus(quickshipProps)) {
        return merge(of(...apiSuccessActions));
      }

      apiSuccessActions.push(
        BulkActions.bulkSetActiveLabelBatchStatusStep({
          step: LabelBatchStatusStep.Processing.ordinal,
          batchId: labelBatchIds[0].toString(),
          batchIds: labelBatchIds.map((lbId) => lbId.toString()),
        }),
      );
      return merge(of(...apiSuccessActions));
    }

    NotificationService.removeNotification(purchasingNotificationId);
    purchasingNotificationId = '';

    if (finalMessage) {
      const labelBatchId = finalMessage.status.BatchID;
      const fpIdsWithLabelsCreated = getFpidsWithLabelsCreated(included, errored);
      const sellerLabelProviderIds = fpIdsWithLabelsCreated.map((fulfillmentPlanId) => {
        return Global.selectEntitiesFulfillmentPlanSellerLabelProviderID(state, { fulfillmentPlanId });
      });
      const hasWalletProvider = sellerLabelProviderIds.some((sellerLabelProviderId) => {
        return Global.selectSellerLabelProviderIsWalleted(state, {
          sellerLabelProviderId,
        });
      });
      trackBulkLabelCreationComplete(
        fpIdsWithLabelsCreated,
        errored,
        excluded,
        labelBatchId,
        hasWalletProvider,
        getPlansWithDangerousGoods(fpIdsWithLabelsCreated, state),
      );

      const finalMessageActions = [
        of(BulkActions.markLabelBatchProcessed(labelBatchId, [])),
        of(BulkActions.clearCreateLabel(labelBatchId)),
        of(BulkActions.finishProcessingCreateLabel(included, excluded, errored, andPrint, { finalMessage })),
      ];

      if (!determineShouldShowLabelBatchStatus(quickshipProps)) {
        showPurchasingLabelsSuccessMessage({
          successCount: finalMessage.status.Completed,
          warningCount: finalMessage.status.Warnings,
        });
      }

      if (quickshipShouldHideLabelBatchStatusModal) {
        ModalService.closeModalById(lbsModalId);
        return concat(
          ...finalMessageActions,
          handleBulkPurchaseEnd({
            includedFulfillmentPlanIds: included,
            excludedFulfillmentPlanIds: excluded,
            erroredFulfillmentPlanIds: errored,
            andPrint,
          }),
        );
      }

      return concat(...finalMessageActions, of(Actions.endBulkPurchase(included, excluded, errored, andPrint, labelBatchId)));
    }

    if (errorMessage && !determineShouldShowLabelBatchStatus(quickshipProps)) {
      const { status } = errorMessage;
      const labelBatchId = status.BatchID;

      const erroredIdsFromResponse = errorResponse.map((item) => item.fulfillmentPlanId);
      const combinedErrored = [...new Set([...errored.map((e) => e.toString()), ...erroredIdsFromResponse])];
      const includedCleaned = getFpidsWithLabelsCreated(included, combinedErrored);

      if (combinedErrored.length > 0) {
        if (!includedCleaned.length) {
          // only errors
          showErrorWithBulkOrdersNotification(combinedErrored.length);
        } else {
          // at least some success
          showPurchasingLabelsSuccessMessage({
            successCount: includedCleaned.length,
            errorCount: combinedErrored.length,
            warningCount: status.Warnings,
          });
        }
      }

      ModalService.closeModalById(lbsModalId);
      const sellerLabelProviderIds = includedCleaned.map((fulfillmentPlanId) => {
        return Global.selectEntitiesFulfillmentPlanSellerLabelProviderID(state, { fulfillmentPlanId });
      });
      const hasWalletProvider = sellerLabelProviderIds.some((sellerLabelProviderId) => {
        return Global.selectSellerLabelProviderIsWalleted(state, {
          sellerLabelProviderId,
        });
      });
      trackBulkLabelCreationComplete(
        includedCleaned,
        errored,
        excluded,
        labelBatchId,
        hasWalletProvider,
        getPlansWithDangerousGoods(includedCleaned, state),
      );

      return concat(
        of(BulkActions.markLabelBatchProcessed(labelBatchId, [])),
        of(BulkActions.clearCreateLabel(labelBatchId)),
        of(BulkActions.finishProcessingCreateLabel(includedCleaned, excluded, combinedErrored, andPrint, { finalMessage: errorMessage })),
        of(Actions.endBulkPurchase(includedCleaned, excluded, combinedErrored, andPrint, labelBatchId)),
      );
    }

    const updatedErrored = errorResponse.reduce((errArr, errorItem) => {
      if (!CODES_THAT_SHOW_COST_REVIEW.includes(errorItem.errorCode)) {
        errArr.push(errorItem.fulfillmentPlanId);
      }

      return errArr;
    }, []);
    const fpIdsWithLabelsCreated = getFpidsWithLabelsCreated(included, errored);
    const sellerLabelProviderIds = fpIdsWithLabelsCreated.map((fulfillmentPlanId) => {
      return Global.selectEntitiesFulfillmentPlanSellerLabelProviderID(state, { fulfillmentPlanId });
    });
    const hasWalletProvider = sellerLabelProviderIds.some((sellerLabelProviderId) => {
      return Global.selectSellerLabelProviderIsWalleted(state, {
        sellerLabelProviderId,
      });
    });
    trackBulkLabelCreationComplete(
      fpIdsWithLabelsCreated,
      errored,
      excluded,
      '',
      hasWalletProvider,
      getPlansWithDangerousGoods(fpIdsWithLabelsCreated, state),
    );

    return concat(
      of(BulkActions.finishProcessingCreateLabel(included, excluded, updatedErrored, andPrint, { finalMessage })),
      of(Actions.endBulkPurchase(included, excluded, updatedErrored, andPrint)),
    );
  };
};
